import type { FilterRange } from 'api/fragments';
import { withErrorBoundary } from 'components/ErrorBoundary';
import AutocompleteInput from 'components/Globals/Base/AutocompleteInput';
import Dropdown, { type Option } from 'components/Globals/Base/Dropdown';
import { RadioGroup } from 'components/Globals/Base/RadioGroup';
import RangeInput from 'components/Globals/Base/RangeInput';
import Filters from 'components/Globals/Filters';
import type React from 'react';
import { memo, useEffect, useState } from 'react';
import { UIFragments } from 'utils/log/constants/uiFragments';

export enum SortingOptions {
  RANK = 'Order/Rank',
  ALPHABETICALLY = 'A - Z',
}

export enum FilterKeys {
  searchQuery = 'searchQuery',
  creditsRange = 'creditsRange',
  gmatRequirement = 'gmatRequirement',
  locations = 'locations',
  price = 'price',
  sortBy = 'sortBy',
}

export const FilterFields = {
  [FilterKeys.searchQuery]: '',
  [FilterKeys.creditsRange]: 'Credits required to graduate',
  [FilterKeys.gmatRequirement]: 'GMAT requirement',
  [FilterKeys.price]: 'Tuition',
  [FilterKeys.locations]: 'Location',
  [FilterKeys.sortBy]: 'Sort By',
};

export interface ProgramsFilterOptions {
  [FilterKeys.searchQuery]: string;
  [FilterKeys.sortBy]: string[];
  [FilterKeys.locations]: Option[];
  [FilterKeys.creditsRange]?: FilterRange;
  [FilterKeys.price]?: FilterRange;
  [FilterKeys.gmatRequirement]?: string[];
}

export interface ProgramsFilters {
  [FilterKeys.searchQuery]: string;
  [FilterKeys.sortBy]: SortingOptions;
  [FilterKeys.locations]: string;
  [FilterKeys.creditsRange]?: FilterRange;
  [FilterKeys.price]?: FilterRange;
  [FilterKeys.gmatRequirement]?: string;
}

interface Props {
  filterOptions: ProgramsFilterOptions;
  priceLabel: string;
  defaultFilters: ProgramsFilters;
  onApplyFiltersClick: (value: ProgramsFilters) => void;
  onResetFiltersClick: () => void;
  appliedFilters: ProgramsFilters;
  shouldUpdateFilters: boolean;
  setShouldUpdateFilters: React.Dispatch<React.SetStateAction<boolean>>;
  resultsCount: number;
}

const ProgramsRankingsFilters = ({
  filterOptions,
  priceLabel,
  defaultFilters,
  onApplyFiltersClick,
  onResetFiltersClick,
  appliedFilters,
  shouldUpdateFilters,
  setShouldUpdateFilters,
  resultsCount,
}: Props) => {
  const [selectedFilters, setSelectedFilters] = useState<ProgramsFilters>(defaultFilters);
  const { sortBy, creditsRange, gmatRequirement, locations, price } = filterOptions;

  useEffect(() => {
    if (shouldUpdateFilters) {
      setSelectedFilters(appliedFilters);
      setShouldUpdateFilters(false);
    }
  }, [shouldUpdateFilters, appliedFilters, setShouldUpdateFilters]);

  const updateSelectedFilters = (selectedFilter: Partial<ProgramsFilters>) => {
    setSelectedFilters((prev) => ({ ...prev, ...selectedFilter }));
  };

  const getOptions = (options: string[]) => options.map((option) => ({ label: option, value: option }));

  return (
    <Filters<ProgramsFilters>
      defaultData={defaultFilters}
      disclaimer='*Please note that not all schools offer this specific type of information.'
      onApplyFiltersClick={onApplyFiltersClick}
      onResetFiltersClick={onResetFiltersClick}
      selectedFilters={selectedFilters}
      setSelectedFilters={setSelectedFilters}
      variant='brand'
      resultsCount={resultsCount}
      className='programs-filters'
    >
      <Dropdown
        className='dropdown'
        id='sort-by-dropdown'
        size='small'
        label={FilterFields[FilterKeys.sortBy]}
        placeholder='Select sorting options'
        selected={selectedFilters[FilterKeys.sortBy]}
        onOptionClick={(val) => updateSelectedFilters({ [FilterKeys.sortBy]: val as SortingOptions })}
        list={getOptions(sortBy)}
      />
      <AutocompleteInput
        options={locations}
        id='locations-autocomplete'
        label={FilterFields[FilterKeys.locations]}
        placeholder='City or State'
        size={{ default: 'small' }}
        selectedOption={selectedFilters[FilterKeys.locations]}
        onOptionClick={(val) => updateSelectedFilters({ [FilterKeys.locations]: val })}
      />
      {gmatRequirement && (
        <RadioGroup
          label={FilterFields[FilterKeys.gmatRequirement]}
          options={getOptions(gmatRequirement)}
          selectedOption={selectedFilters[FilterKeys.gmatRequirement] ?? ''}
          ariaLabelledBy='gmat-requirements'
          name='gmat-requirements'
          onOptionChange={(val) => updateSelectedFilters({ [FilterKeys.gmatRequirement]: val })}
        />
      )}
      {creditsRange && (
        <RangeInput
          className='credits-range'
          id='credits-range'
          label={FilterFields[FilterKeys.creditsRange]}
          size='small'
          step={1}
          value={selectedFilters[FilterKeys.creditsRange] ?? { min: creditsRange.min, max: creditsRange.max }}
          minValue={creditsRange.min}
          maxValue={creditsRange.max}
          onChange={(max, min) => updateSelectedFilters({ [FilterKeys.creditsRange]: { max, min } })}
          variant='brand'
        />
      )}
      {price && (
        <RangeInput
          id='price-range'
          className='price-range'
          label={priceLabel}
          size='small'
          step={50}
          value={selectedFilters[FilterKeys.price] ?? { min: price.min, max: price.max }}
          minValue={price.min}
          maxValue={price.max}
          onChange={(max, min) => updateSelectedFilters({ [FilterKeys.price]: { max, min } })}
          isCurrency
          variant='brand'
        />
      )}
    </Filters>
  );
};

export default memo(withErrorBoundary(ProgramsRankingsFilters, { fragment: UIFragments.EDU_RANKINGS_FILTERS }));
